import type React from "react"

import {cn} from "@/lib/cn"

/**
 * DIZED card surfaces. `Card` is the generic gradient surface with the lit
 * edge; `GameCard*` parts compose the grid game card from the style guide:
 * art (square), optional highlight bar, 2-line title, icon content bar.
 */
export const Card = ({className, ...props}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "overflow-hidden rounded-card card-elev text-foreground shadow-card-lit",
      className,
    )}
    {...props}
  />
)

export const CardContent = ({className, ...props}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("p-4", className)} {...props} />
)

/** Square art area; place an <Image fill> or <img> inside.
 * bg-muted keeps the slot from flashing empty while lazy art loads. */
export const GameCardArt = ({className, ...props}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("relative aspect-square w-full overflow-hidden bg-muted", className)}
    {...props}
  />
)

/**
 * Two-line clamped title. Exact height (padding + 2 lines) backs up
 * line-clamp: Chrome's standardized line-clamp can drop the legacy
 * -webkit-box path (computed display becomes flow-root), letting a third
 * line paint — the fixed height crops it regardless.
 */
export const GameCardTitle = ({className, ...props}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "line-clamp-2 h-11 overflow-hidden px-3 pt-2 pb-1 font-sans text-xs font-semibold leading-4 text-foreground",
      className,
    )}
    {...props}
  />
)

/** Bottom meta bar: quiet on the neutral card so the art carries the color. */
export const GameCardBar = ({className, ...props}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex h-7 items-center gap-1.5 border-t border-[color:var(--card-border)] px-3 text-[10px] font-semibold text-muted-foreground",
      "[&_svg]:size-[13px]",
      className,
    )}
    {...props}
  />
)

/**
 * Grid game card: elevated neutral surface, art-forward. Hover lifts with an
 * aqua glow and the art zooms (reduced-motion safe via app.css).
 *
 * The lift is group-hover-driven: hover state must live on a static `.group`
 * ancestor (the wrapping link), never on the card itself — a self-hovered
 * translate moves the hit area out from under a bottom-edge cursor and the
 * card oscillates.
 */
export const GameCard = ({className, ...props}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "group w-40 overflow-hidden rounded-[14px] card-elev text-foreground shadow-card-lit",
      "transition-[transform,box-shadow] duration-200 ease-out group-hover:-translate-y-1 group-hover:shadow-glow",
      className,
    )}
    {...props}
  />
)
